/**
 * Create ad — one screen, six steps, the four creates of a complete ad in order.
 *
 * 🔴 Until 2026-10-21 the path to an ad ran across four panels: the campaign form,
 * the line item form under the campaign's row, Targeting under the line item's row,
 * Creative beside it — with budgets typed in micro units and flights typed as ISO
 * strings (the owner, 20–21.10: «I can create a campaign, but I cannot add an ad
 * group or an ad»). This screen asks the same questions in words, shows what will be
 * sent before anything is, and then sends the four requests one at a time, waiting
 * for each answer. Those panels are untouched: they edit what exists.
 *
 * The decisions are in `lib/create-ad.ts` and the words in `lib/create-ad-copy.ts`;
 * this file renders them. The one thing it decides is the order of the four sends,
 * and `create-ad-copy.test.ts` reads that order off this file as text.
 *
 * 🔴 A run that meets anything but `created` stops and never retries: the ids made
 * so far are shown with the contract's own sentence, and the way on is the Campaigns
 * screen. Start is two separate presses afterwards, gated as the Targeting panel
 * gates them, and what X reports is read back rather than assumed.
 *
 * 🔴 The run is held in `lib/create-locks.ts` under the ad account, not in this page: the
 * route away unmounts it, and Create (paused) used to come back for a draft whose campaign
 * exists or may. A finished run is let go when the page opens next (`finishedRun`). Each
 * parent it sends is marked in flight, and a locking answer held, under the key that
 * parent's own form reads — the Campaigns page's Create, the line item section's, Add ad
 * on the row — so none of them offers again what this run may have made.
 */
import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AdStep } from "../components/AdStep.tsx";
import { PrimaryTargeting } from "../components/PrimaryTargeting.tsx";
import { runAd } from "../lib/ad-run.ts";
import {
	type CustomAudience,
	type LineItemObjective,
	type TargetingDevice,
	type TargetingLanguage,
	type TargetingLocation,
	type TargetingNetworkOperator,
	type TargetingPlatform,
	UnauthorizedError,
	type XAccount,
	api,
} from "../lib/api.ts";
import type { Standing } from "../lib/api.ts";
import {
	enablePrompt,
	fundingOptions,
	hostWarning,
	mismatchText,
	servableText,
} from "../lib/campaign-copy.ts";
import { CANCEL } from "../lib/campaign-manage-copy.ts";
import { canWriteAccount, createOutcome, createThrew, statusOutcome } from "../lib/campaigns.ts";
import {
	ACCOUNT_HELP,
	ADD_COUNTRY,
	AD_GROUP_DAILY_LABEL,
	AD_GROUP_HELP,
	AD_GROUP_NAME_HELP,
	AD_GROUP_NAME_LABEL,
	AD_GROUP_TOTAL_LABEL,
	AGE_ALL,
	AGE_LABEL,
	AUDIENCES_CHOSEN,
	AUDIENCES_EMPTY,
	AUDIENCES_HELP,
	AUDIENCES_NOT_READ,
	AUDIENCES_NOT_TARGETABLE,
	AUDIENCE_HELP,
	AUDIENCE_LABEL,
	AUDIENCE_MORE_HELP,
	AUDIENCE_NONE,
	BACK,
	BUDGET_ON_HELP,
	BUDGET_ON_LABEL,
	BUDGET_ON_OPTIONS,
	CAMPAIGN_HELP,
	CARRIERS_CHOSEN,
	CARRIERS_NOT_READ,
	CARRIER_COUNTRY_LABEL,
	CARRIER_LABEL,
	CARRIER_NEEDS_COUNTRY,
	CARRIER_NONE,
	COUNTRIES_CHOSEN,
	COUNTRIES_EXCLUDED,
	COUNTRY_LABEL,
	COUNTRY_PLACEHOLDER,
	CREATE_LABEL,
	DEVICES_CHOSEN,
	DEVICES_NOT_READ,
	DEVICE_LABEL,
	DEVICE_MORE_HELP,
	DEVICE_NONE,
	DEVICE_SEARCH_LABEL,
	DONE,
	END_LABEL,
	EVERYONE_NOTE,
	EXCLUDE_COUNTRY,
	EXCLUDE_HELP,
	GENDER_ALL,
	GENDER_LABEL,
	INSTRUMENT_LABEL,
	INTRO,
	LANGUAGES_CHOSEN,
	LANGUAGES_NOT_READ,
	LANGUAGE_LABEL,
	LANGUAGE_NONE,
	NAME_LABEL,
	NEXT,
	NO_MATCH,
	OBJECTIVE_LABEL,
	OBJECTIVE_OPTIONS,
	OPEN_IN_CAMPAIGNS,
	OS_CHOSEN,
	OS_LABEL,
	OS_NONE,
	OS_NOT_READ,
	READ_ONLY,
	REMOVE_COUNTRY,
	REVIEW_HELP,
	REVIEW_ROWS,
	RUN_ORDER,
	RUN_ROWS,
	SCHEDULE_HELP,
	SCHEDULE_PREFILLED,
	SEARCHING,
	START_AD_GROUP,
	START_CAMPAIGN,
	START_CAMPAIGN_WAITS,
	START_HEADING,
	START_LABEL,
	START_ORDER,
	STEP_NAMES,
	TITLE,
	accountNotAccepted,
	adGroupDailyHelp,
	adGroupOverCampaign,
	adGroupTotalHelp,
	audienceNotTargetable,
	budgetHelper,
	budgetLabel,
	budgetSummary,
	countriesSummary,
	everyoneLabel,
	instrumentWarning,
	objectiveHint,
	objectiveSummary,
	optionalSummary,
	problemText,
	scheduleSummary,
	sentAs,
	stepText,
	stoppedText,
	writtenAdSummary,
} from "../lib/create-ad-copy.ts";
import {
	type BudgetOn,
	type Draft,
	type Run,
	SEARCH_DEBOUNCE_MS,
	type Step,
	type StepState,
	bidOverCampaign,
	budgetField,
	campaignCreate,
	campaignStep,
	cardMode,
	createdSoFar,
	criteriaMade,
	criteriaToSend,
	criterionStep,
	defaultTweetName,
	freshRun,
	lineItemCreate,
	lineItemStep,
	mayCreateAgain,
	newDraft,
	runDone,
	runStopped,
	somethingMayExist,
	stepProblem,
	threwStep,
	tweetText,
	utcIso,
} from "../lib/create-ad.ts";
import {
	adPart,
	addAdLockKey,
	campaignLockKey,
	createAdLockKey,
	finishedRun,
	holdIfLocking,
	holdOutcome,
	lineItemLockKey,
	useHeldOutcome,
	whileSent,
} from "../lib/create-locks.ts";
import { tweetLabel } from "../lib/creative-copy.ts";
import { approvalStatus, syncedLabel } from "../lib/format.ts";
import { OBJECTIVE_NOTE, PRICE_IS_X } from "../lib/line-item-copy.ts";
import {
	CAP_HELP,
	CAP_LABEL,
	CAP_NOT_FOR_OBJECTIVE,
	DELIVERY_HEADING,
	DELIVERY_HELP,
	EXPANSION_HELP,
	EXPANSION_LABEL,
	EXPANSION_NONE,
	PACING_ACCELERATED,
	PACING_HELP,
	PACING_LABEL,
	PACING_NONE,
	PACING_STANDARD,
	WINDOW_LABEL,
	WINDOW_NAMES,
} from "../lib/line-item-delivery-copy.ts";
import {
	AUDIENCE_EXPANSION,
	DURATION_IN_DAYS,
	type DeliveryDraft,
	capOffered,
} from "../lib/line-item-delivery.ts";
import {
	BID_AMOUNT_LABEL,
	BID_LABEL,
	BID_NAMES,
	GOAL_DEFAULT_OPTION,
	GOAL_HELP,
	GOAL_LABEL,
	GOAL_NAMES,
	PAY_BY_LABEL,
	PAY_BY_NOTE,
	PLACEMENTS_HELP,
	PLACEMENTS_LABEL,
	PLACEMENT_NAMES,
	PLACEMENT_TARGETING,
	PRODUCT_FIXED,
	TARGET_NOT_FOR_ENGAGEMENTS,
	TARGET_PAY_BY_OPEN,
	bidAmountHelp,
	bidOverBudget,
	bidSummary,
	goalSummary,
	placementsSummary,
} from "../lib/line-item-fields-copy.ts";
import {
	BID_STRATEGIES,
	type BidStrategy,
	GOALS,
	PLACEMENTS,
	type Placement,
	bidNeedsAmount,
	goalFor,
	strategyFor,
} from "../lib/line-item-fields.ts";
import {
	type PendingStart,
	lineItemCreateOutcome,
	lineItemStatusOutcome,
	startAlreadyAsked,
	startGate,
} from "../lib/line-items.ts";
import { microMoney } from "../lib/money.ts";
import {
	AGE_BUCKETS,
	AGE_ONLY_SEEN,
	GENDER_OPTIONS,
	PLATFORM_DEVICE_OVERLAP,
	START_IS_NOT_DELIVERY,
	ageLabel,
	carrierLabel,
	deviceLabel,
	languageLabel,
	startWarning,
	switchText,
} from "../lib/targeting-copy.ts";
import { useLoad } from "../lib/useLoad.ts";
import { xHost } from "../lib/x-host.ts";
import {
	Badge,
	Button,
	InfoBanner,
	Input,
	Modal,
	Page,
	Panel,
	Select,
	Spinner,
} from "../ui/index.ts";
import { badgeVariant } from "./Overview.tsx";

const message = (e: unknown) => (e instanceof Error ? e.message : String(e));

/** A step's fields in two columns from xl up (principles.md §3), each read top to bottom. */
const TWO_COLUMNS = "grid gap-4 xl:grid-cols-2 xl:items-start";
const COLUMN = "grid content-start gap-4";

export function CreateAd({ standing }: { standing: Standing }) {
	const isPlatform = standing.standing === "platform";
	const [params] = useSearchParams();
	const asked = params.get("account") ?? "";

	const [accounts] = useLoad(() => api.xAccounts());
	const [access] = useLoad(() => (isPlatform ? api.myAccess() : Promise.resolve(null)));
	const [smoke] = useLoad(() => api.smoke());
	const host = xHost(smoke.state === "ready" ? smoke.data.x?.target : null);
	const warning = hostWarning(host);

	const [step, setStep] = useState<Step>(asked ? 1 : 0);
	const [draft, setDraft] = useState<Draft>(() => newDraft(asked, new Date()));
	const set = (patch: Partial<Draft>) => setDraft((d) => ({ ...d, ...patch }));
	/** Shown once Next was pressed on a step that is not complete. */
	const [tried, setTried] = useState(false);

	const list = accounts.state === "ready" ? accounts.data.accounts : [];
	const account: XAccount | null = list.find((a) => a.id === draft.accountId) ?? null;
	const xAccountId = account?.xAccountId ?? "";
	// A step past the account step needs an account: an id from the URL that names
	// none falls back to step 0 rather than to a campaign form under nobody.
	const shown: Step = account ? step : 0;

	const platformGrants = access.state === "ready" && access.data ? access.data.grants : [];
	const writable = account ? canWriteAccount(standing, platformGrants, account.id) : false;

	const [funding, reloadFunding] = useLoad(() =>
		xAccountId ? api.fundingInstruments(xAccountId) : Promise.resolve(null),
	);
	// biome-ignore lint/correctness/useExhaustiveDependencies: a different account is the reason to read again
	useEffect(() => {
		reloadFunding();
	}, [xAccountId, reloadFunding]);
	const instruments =
		funding.state === "ready" && funding.data ? funding.data.fundingInstruments : [];
	const instrument = instruments.find((f) => f.id === draft.fundingInstrumentId) ?? null;
	const currency = instrument?.currency ?? null;
	// X's verdict on the instrument, never a reading of its description (A6, 2026-10-21).
	const cardWarning = instrumentWarning(instrument);

	// ── Audience: the country search, debounced ─────────────────────
	const [query, setQuery] = useState("");
	const [places, setPlaces] = useState<TargetingLocation[] | null>(null);
	const [searching, setSearching] = useState(false);
	const [searchFailed, setSearchFailed] = useState<string | null>(null);
	useEffect(() => {
		const q = query.trim();
		if (q === "") {
			setPlaces(null);
			setSearchFailed(null);
			return;
		}
		// 🔴 Whatever comes back for a query that is no longer the one typed is dropped:
		// the answer to "Pol" landing after the answer to "Poland" would put the wrong
		// list under the right word.
		let stale = false;
		const timer = setTimeout(() => {
			setSearching(true);
			api
				.targetingLocations(q, "COUNTRIES")
				.then((page) => {
					if (stale) return;
					setPlaces(page.locations);
					setSearchFailed(null);
				})
				.catch((e: unknown) => {
					if (stale || e instanceof UnauthorizedError) return;
					setPlaces(null);
					setSearchFailed(message(e));
				})
				.finally(() => {
					if (!stale) setSearching(false);
				});
		}, SEARCH_DEBOUNCE_MS);
		return () => {
			stale = true;
			clearTimeout(timer);
		};
	}, [query]);

	// ── Audience: X's languages, read once when the step first opens ──
	const [languageList, setLanguageList] = useState<TargetingLanguage[] | null>(null);
	const [languagesFailed, setLanguagesFailed] = useState<string | null>(null);
	useEffect(() => {
		if (step !== 3 || languageList !== null) return;
		let stale = false;
		api
			.targetingLanguages("")
			.then((page) => {
				if (!stale) setLanguageList(page.languages);
			})
			.catch((e: unknown) => {
				if (stale || e instanceof UnauthorizedError) return;
				setLanguagesFailed(message(e));
			});
		return () => {
			stale = true;
		};
	}, [step, languageList]);

	// ── Audience: X's operating systems, read once when the step first opens ──
	const [platformList, setPlatformList] = useState<TargetingPlatform[] | null>(null);
	const [platformsFailed, setPlatformsFailed] = useState<string | null>(null);
	useEffect(() => {
		if (step !== 3 || platformList !== null) return;
		let stale = false;
		api
			.targetingPlatforms()
			.then((page) => {
				if (!stale) setPlatformList(page.platforms);
			})
			.catch((e: unknown) => {
				if (stale || e instanceof UnauthorizedError) return;
				setPlatformsFailed(message(e));
			});
		return () => {
			stale = true;
		};
	}, [step, platformList]);

	// ── Audience: the device search, debounced as the country search is ──
	const [deviceQuery, setDeviceQuery] = useState("");
	const [deviceList, setDeviceList] = useState<TargetingDevice[] | null>(null);
	const [devicesFailed, setDevicesFailed] = useState<string | null>(null);
	useEffect(() => {
		const q = deviceQuery.trim();
		if (q === "") {
			setDeviceList(null);
			setDevicesFailed(null);
			return;
		}
		// Whatever comes back for a query no longer typed is dropped, as for countries.
		let stale = false;
		const timer = setTimeout(() => {
			api
				.targetingDevices(q)
				.then((page) => {
					if (stale) return;
					setDeviceList(page.devices);
					setDevicesFailed(null);
				})
				.catch((e: unknown) => {
					if (stale || e instanceof UnauthorizedError) return;
					setDeviceList(null);
					setDevicesFailed(message(e));
				});
		}, SEARCH_DEBOUNCE_MS);
		return () => {
			stale = true;
			clearTimeout(timer);
		};
	}, [deviceQuery]);

	// ── Audience: carriers, read for one chosen country at a time ──
	const [carrierCountry, setCarrierCountry] = useState("");
	const [carrierList, setCarrierList] = useState<TargetingNetworkOperator[] | null>(null);
	const [carriersFailed, setCarriersFailed] = useState<string | null>(null);
	useEffect(() => {
		setCarrierList(null);
		setCarriersFailed(null);
		// 🔴 No country, no read: X's list without one is the US list (CARRIER_NEEDS_COUNTRY).
		if (carrierCountry === "") return;
		let stale = false;
		api
			.targetingNetworkOperators(carrierCountry)
			.then((page) => {
				if (!stale) setCarrierList(page.networkOperators);
			})
			.catch((e: unknown) => {
				if (stale || e instanceof UnauthorizedError) return;
				setCarriersFailed(message(e));
			});
		return () => {
			stale = true;
		};
	}, [carrierCountry]);
	// ── Audience: the account's own custom audiences, read when the step opens for it ──
	const [audienceList, setAudienceList] = useState<{
		account: string;
		rows: CustomAudience[];
	} | null>(null);
	const [audiencesFailed, setAudiencesFailed] = useState<string | null>(null);
	useEffect(() => {
		if (step !== 3 || !xAccountId || audienceList?.account === xAccountId) return;
		let stale = false;
		setAudiencesFailed(null);
		api
			.customAudiences(xAccountId)
			.then((page) => {
				if (!stale) setAudienceList({ account: xAccountId, rows: page.customAudiences });
			})
			.catch((e: unknown) => {
				if (stale || e instanceof UnauthorizedError) return;
				setAudiencesFailed(message(e));
			});
		return () => {
			stale = true;
		};
	}, [step, xAccountId, audienceList]);
	// 🔴 Only what X reports as targetable is offered; the rest is shown with X's reasons.
	const audiencesHere = audienceList?.account === xAccountId ? audienceList.rows : null;
	const targetableAudiences = (audiencesHere ?? []).filter((a) => a.targetable);
	const untargetableAudiences = (audiencesHere ?? []).filter((a) => !a.targetable);

	const carrierCountries = [
		...new Set(draft.countries.map((c) => c.countryCode).filter((c): c is string => !!c)),
	];

	// ── Review: the run ─────────────────────────────────────────────
	const [run, setRun] = useHeldOutcome<Run>(createAdLockKey(xAccountId));
	// biome-ignore lint/correctness/useExhaustiveDependencies: when the page opens on an account — a run that attached its ad is done with; one that stopped or is still sending keeps the lock
	useEffect(() => {
		if (run !== null && finishedRun(run)) setRun(null);
	}, [xAccountId]);
	const [creating, setCreating] = useState(false);
	/** The latest state of the run, so each await builds on what the one before set. */
	const held = useRef<Run | null>(null);
	const update = (patch: Partial<Run>) => {
		const next = { ...(held.current ?? freshRun([])), ...patch };
		held.current = next;
		setRun(next);
	};
	const targetingAt = (i: number, state: StepState) => {
		const rows = (held.current ?? freshRun([])).targeting.map((t, j) =>
			j === i ? { ...t, state } : t,
		);
		update({ targeting: rows });
	};
	const mediaAt = (i: number, state: StepState) => {
		const rows = (held.current ?? freshRun([])).media.map((m, j) =>
			j === i ? { ...m, state } : m,
		);
		update({ media: rows });
	};
	const pause = (seconds: number) =>
		new Promise<void>((resolve) => setTimeout(resolve, Math.min(seconds, 5) * 1000));

	/**
	 * 🔴 The four sends, in order, each waiting for the one before, and a stop at the
	 * first answer that is not `created`. No loop retries anything: an object that
	 * `exists` or may exist is one a second request would duplicate.
	 */
	async function create() {
		if (!account || !writable || creating || !mayCreateAgain(run)) return;
		const campaignBody = campaignCreate(xAccountId, draft);
		if (!campaignBody.ok) return;
		held.current = freshRun(criteriaToSend(draft), draft.adFiles, draft.adMode, cardMode(draft));
		const sending = held.current.targeting;
		setRun(held.current);
		setCreating(true);
		try {
			update({ campaign: { kind: "running" } });
			const campaignKey = campaignLockKey(xAccountId);
			const campaign = await whileSent(campaignKey, () =>
				api.createCampaign(campaignBody.value).then(
					(a) => {
						holdIfLocking(campaignKey, createOutcome(a.status, a.body));
						return campaignStep(a.status, a.body);
					},
					(e: unknown) => {
						if (!(e instanceof UnauthorizedError))
							holdIfLocking(campaignKey, createThrew(message(e)));
						return threwStep(message(e));
					},
				),
			);
			update({ campaign });
			if (campaign.kind !== "done") return;

			const lineItemBody = lineItemCreate(xAccountId, campaign.id, draft);
			if (!lineItemBody.ok) return;
			update({ lineItem: { kind: "running" } });
			const lineItemKey = lineItemLockKey(xAccountId, campaign.id);
			const lineItem = await whileSent(lineItemKey, () =>
				api.createLineItem(lineItemBody.value).then(
					(a) => {
						holdIfLocking(lineItemKey, lineItemCreateOutcome(a.status, a.body));
						return lineItemStep(a.status, a.body);
					},
					(e: unknown) => {
						if (!(e instanceof UnauthorizedError))
							holdIfLocking(lineItemKey, createThrew(message(e)));
						return threwStep(message(e));
					},
				),
			);
			update({ lineItem });
			if (lineItem.kind !== "done") return;

			for (let i = 0; i < sending.length; i++) {
				targetingAt(i, { kind: "running" });
				const { targetingType, targetingValue, operatorType } = sending[i];
				const criterion = await api
					.createTargetingCriterion({
						account: xAccountId,
						lineItemId: lineItem.id,
						// A place goes without a type, as it always has; the worker reads that as LOCATION.
						...(targetingType === "LOCATION" ? {} : { targetingType }),
						// An excluded country alone names its operator; every other criterion is EQ, unnamed.
						...(operatorType === "NE" ? { operatorType } : {}),
						targetingValue,
					})
					.then(
						(a) => criterionStep(a.status, a.body),
						(e: unknown) => threwStep(message(e)),
					);
				targetingAt(i, criterion);
				if (criterion.kind !== "done") return;
			}

			/*
			 * 🔴 The ad written here: one upload per file, each waited for — and asked about
			 * again while X reports processing — then the tweet, then the promote. Nothing
			 * is uploaded before this point; the files sat in the draft.
			 */
			// The ad's run — upload, tweet, attach — is the module's, shared with the card's Add ad
			// (M2, 2026-10-21): the same three calls in the same order, each awaited, stopping at
			// the first step that is not done; the states land in `held` as the rows above do.
			// Each of its states is held under the line item too, as Add ad's own run is, so Add
			// ad on that row reads what this run may have made there.
			const toAddAd = () =>
				holdOutcome(addAdLockKey(xAccountId, lineItem.id), adPart(held.current ?? freshRun([])));
			await runAd(
				api,
				{ account: xAccountId, lineItemId: lineItem.id, objective: draft.objective, draft },
				{
					mediaAt: (i, state) => {
						mediaAt(i, state);
						toAddAd();
					},
					card: (state) => {
						update({ card: state });
						toAddAd();
					},
					tweet: (state) => {
						update({ tweet: state });
						toAddAd();
					},
					ad: (state) => {
						update({ ad: state });
						toAddAd();
					},
					held: () => held.current ?? freshRun([]),
				},
				pause,
			);
		} finally {
			setCreating(false);
		}
	}

	// ── Start: two presses, read back ───────────────────────────────
	const [switching, setSwitching] = useState(false);
	const [pending, setPending] = useState<PendingStart | null>(null);
	const [lineItemSwitch, setLineItemSwitch] = useState<string | null>(null);
	/** X reported the line item ACTIVE — the one thing that offers the campaign's Start. */
	const [lineItemStarted, setLineItemStarted] = useState(false);
	const [campaignSwitch, setCampaignSwitch] = useState<{
		variant: "success" | "danger";
		title: string;
		body: string | null;
	} | null>(null);

	async function startLineItem() {
		if (!run || run.lineItem.kind !== "done" || switching) return;
		const lineItemId = run.lineItem.id;
		// The Targeting panel's gate, with the criteria this run made: an empty list
		// names its price before anything is sent, and the second press sends.
		const warn = startWarning(startGate(criteriaMade(run)));
		if (warn !== null && !startAlreadyAsked(lineItemId, warn, pending)) {
			setPending({ lineItemId, text: warn });
			return;
		}
		setPending(null);
		setSwitching(true);
		try {
			const { status, body } = await api.setLineItemStatus(lineItemId, xAccountId, "ACTIVE");
			const outcome = lineItemStatusOutcome("ACTIVE", status, body);
			setLineItemSwitch(switchText(outcome));
			if (outcome.kind === "reported" && outcome.reported === "ACTIVE") setLineItemStarted(true);
		} catch (e) {
			if (e instanceof UnauthorizedError) return;
			setLineItemSwitch(message(e));
		} finally {
			setSwitching(false);
		}
	}

	/** The question before the campaign's ACTIVE, open in the Modal until it is answered. */
	const [confirming, setConfirming] = useState<string | null>(null);

	function startCampaign() {
		if (!run || run.campaign.kind !== "done" || !lineItemStarted || switching || !account) return;
		const daily = budgetField(draft.daily);
		const total = budgetField(draft.total);
		// The Campaigns screen's own confirmation before ACTIVE: the name, the account,
		// both budgets as X holds them, and the host the switch goes to. It is asked in the
		// Modal below, and the switch is sent only from its confirm press.
		const prompt = enablePrompt(
			{
				name: draft.name,
				currency,
				dailyBudgetAmountLocalMicro: daily.kind === "amount" ? daily.micro : null,
				totalBudgetAmountLocalMicro: total.kind === "amount" ? total.micro : null,
			},
			account,
			host,
		);
		setConfirming(prompt);
	}

	async function sendCampaignStart() {
		setConfirming(null);
		if (!run || run.campaign.kind !== "done" || !lineItemStarted || switching || !account) return;
		setSwitching(true);
		try {
			const { status, body } = await api.setCampaignStatus(run.campaign.id, {
				account: xAccountId,
				status: "ACTIVE",
			});
			const o = statusOutcome("ACTIVE", status, body);
			if (o.kind === "set")
				setCampaignSwitch({
					variant: "success",
					title: `${o.id} is ${o.entityStatus}.`,
					body: servableText(o.servable, o.reasonsNotServable),
				});
			else if (o.kind === "mismatch")
				setCampaignSwitch({
					variant: "danger",
					title: mismatchText(o.asked, o.reported),
					body: o.error,
				});
			else
				setCampaignSwitch({
					variant: "danger",
					title: "The status was not changed.",
					body: o.error,
				});
		} catch (e) {
			if (e instanceof UnauthorizedError) return;
			setCampaignSwitch({
				variant: "danger",
				title: "The status change got no answer — look before switching again.",
				body: message(e),
			});
		} finally {
			setSwitching(false);
		}
	}

	// ── Navigation ──────────────────────────────────────────────────
	const problem = stepProblem(shown, draft);
	const locked = run !== null && !mayCreateAgain(run);
	function next() {
		if (problem !== null) {
			setTried(true);
			return;
		}
		setTried(false);
		if (shown < 5) setStep((shown + 1) as Step);
	}
	function back() {
		setTried(false);
		if (shown > 0) setStep((shown - 1) as Step);
	}

	const dailyField = budgetField(draft.daily);
	const totalField = budgetField(draft.total);
	const adGroupTotalField = budgetField(draft.lineItemTotal);
	const adGroupDailyField = budgetField(draft.lineItemDaily);
	const overCampaign = adGroupOverCampaign(
		adGroupTotalField.kind === "amount" ? adGroupTotalField.micro : null,
		totalField.kind === "amount" ? totalField.micro : null,
	);
	const bidField = budgetField(draft.bidAmount);
	const overBudget = bidOverCampaign(draft);
	const startIso = utcIso(draft.start);
	const endIso = utcIso(draft.end);
	const status = account ? approvalStatus(account.approvalStatus) : null;
	const notAccepted = account ? accountNotAccepted(account.approvalStatus) : null;

	return (
		<Page fill>
			{/*
			 * Three columns (audits/2026-10-29/gui/principles.md §3): the steps, the step's
			 * fields with Back / Next fixed under them, and what will be sent beside both on
			 * every step. Below lg the three stack and the column scrolls.
			 */}
			<div className="grid h-full min-h-0 gap-3 overflow-y-auto lg:grid-cols-[12.5rem_minmax(0,1fr)_21rem] lg:overflow-hidden">
				<Panel title={TITLE}>
					<ol className="grid gap-1 text-sm">
						{STEP_NAMES.map((name, i) => (
							<li
								key={name}
								className={`rounded-md px-2 py-1.5 ${
									i === shown
										? "bg-primary-soft font-semibold text-primary-soft-fg"
										: i < shown
											? "text-content"
											: "text-muted"
								}`}
							>
								{i + 1}. {name}
							</li>
						))}
					</ol>
					<p className="mt-auto text-xs text-muted">{INTRO}</p>
				</Panel>

				<Panel
					title={`${shown + 1}. ${STEP_NAMES[shown]}`}
					footer={
						<div className="flex w-full flex-wrap items-center gap-3">
							{tried && problem !== null && (
								<InfoBanner variant="danger" title={problemText(problem)} className="w-full" />
							)}
							<div className="ml-auto flex items-center gap-3">
								{shown > 0 && (
									<Button type="button" variant="outline" disabled={creating} onClick={back}>
										{BACK}
									</Button>
								)}
								{shown < 5 && (
									<Button type="button" disabled={!account} onClick={next}>
										{NEXT}
									</Button>
								)}
							</div>
						</div>
					}
				>
					{accounts.state === "failed" && (
						<InfoBanner variant="danger" title="Ad accounts could not be read.">
							{accounts.why}
						</InfoBanner>
					)}

					{shown === 0 && (
						<section className="grid gap-4">
							<p className="text-sm text-muted">{ACCOUNT_HELP}</p>
							<Select
								label="Ad account"
								value={draft.accountId}
								onChange={(e) => {
									// Another account's instruments, tweets and custom audiences are not this
									// one's: the picks are emptied here, and the Ad step is keyed on the account
									// so its list goes too.
									set({
										accountId: e.target.value,
										fundingInstrumentId: "",
										tweet: null,
										audiences: [],
									});
								}}
								placeholder="Choose an ad account"
								disabled={list.length === 0}
								options={list.map((a) => ({ value: a.id, label: `${a.name} · ${a.xAccountId}` }))}
							/>
							{account && status && (
								<div className="grid gap-1">
									<div>
										<Badge variant={badgeVariant(status.tone)}>{status.label}</Badge>
									</div>
									<p className="text-xs text-muted">{syncedLabel(account.syncedAt)}</p>
								</div>
							)}
							{notAccepted && (
								<InfoBanner variant="warning" title={notAccepted.title}>
									{notAccepted.body}
								</InfoBanner>
							)}
							{account && !writable && <p className="text-sm text-muted">{READ_ONLY}</p>}
						</section>
					)}

					{shown === 1 && (
						<section className={TWO_COLUMNS}>
							<div className={COLUMN}>
								<p className="text-sm text-muted">{CAMPAIGN_HELP}</p>
								<Input
									label={NAME_LABEL}
									value={draft.name}
									onChange={(e) => set({ name: e.target.value })}
								/>
								<Select
									label={INSTRUMENT_LABEL}
									value={draft.fundingInstrumentId}
									onChange={(e) => set({ fundingInstrumentId: e.target.value })}
									placeholder={
										funding.state === "loading"
											? "Reading funding instruments…"
											: fundingOptions(instruments).length === 0
												? "None under this account can fund"
												: "Choose a funding instrument"
									}
									options={fundingOptions(instruments)}
								/>
								{funding.state === "failed" && (
									<InfoBanner variant="danger" title="Funding instruments could not be read.">
										{funding.why}
									</InfoBanner>
								)}
								{cardWarning && (
									<InfoBanner variant={cardWarning.variant} title={cardWarning.title}>
										{cardWarning.body}
									</InfoBanner>
								)}
							</div>
							<div className={COLUMN}>
								<Select
									label={BUDGET_ON_LABEL}
									value={draft.budgetOn}
									onChange={(e) => set({ budgetOn: e.target.value as BudgetOn })}
									options={BUDGET_ON_OPTIONS.map((o) => ({ ...o }))}
								/>
								<p className="text-xs text-muted">{BUDGET_ON_HELP}</p>
								{draft.budgetOn === "campaign" && (
									<Input
										label={budgetLabel("Daily")}
										inputMode="decimal"
										value={draft.daily}
										onChange={(e) => set({ daily: e.target.value })}
										helperText={budgetHelper(currency, false)}
										error={dailyField.kind === "invalid" ? problemText("daily-invalid") : undefined}
									/>
								)}
								<Input
									label={budgetLabel("Total")}
									inputMode="decimal"
									value={draft.total}
									onChange={(e) => set({ total: e.target.value })}
									helperText={budgetHelper(currency, true)}
									error={totalField.kind === "invalid" ? problemText("total-invalid") : undefined}
								/>
							</div>
						</section>
					)}

					{shown === 2 && (
						<section className={TWO_COLUMNS}>
							<div className={COLUMN}>
								<p className="text-sm text-muted">{AD_GROUP_HELP}</p>
								<Select
									label={OBJECTIVE_LABEL}
									value={draft.objective}
									onChange={(e) => {
										// A strategy or goal the new objective does not offer is dropped here
										// rather than sent and refused (lib/line-item-fields.ts).
										const objective = e.target.value as LineItemObjective;
										set({
											objective,
											bidStrategy: strategyFor(objective, draft.bidStrategy),
											goal: goalFor(objective, draft.goal),
										});
									}}
									options={OBJECTIVE_OPTIONS.map((o) => ({ ...o }))}
								/>
								<p className="text-xs text-muted">{objectiveHint(draft.objective)}</p>
								<p className="text-sm text-muted">{OBJECTIVE_NOTE}</p>
								<Input
									label={AD_GROUP_NAME_LABEL}
									value={draft.lineItemName}
									onChange={(e) => set({ lineItemName: e.target.value })}
									helperText={AD_GROUP_NAME_HELP}
								/>
								{draft.budgetOn === "ad-group" && (
									<Input
										label={AD_GROUP_DAILY_LABEL}
										inputMode="decimal"
										value={draft.lineItemDaily}
										onChange={(e) => set({ lineItemDaily: e.target.value })}
										helperText={adGroupDailyHelp(currency)}
										error={
											adGroupDailyField.kind === "invalid"
												? problemText("ad-group-daily-invalid")
												: tried && adGroupDailyField.kind === "absent"
													? problemText("ad-group-daily-missing")
													: undefined
										}
									/>
								)}
								<Input
									label={AD_GROUP_TOTAL_LABEL}
									inputMode="decimal"
									value={draft.lineItemTotal}
									onChange={(e) => set({ lineItemTotal: e.target.value })}
									helperText={adGroupTotalHelp(currency)}
									error={
										adGroupTotalField.kind === "invalid"
											? problemText("ad-group-total-invalid")
											: undefined
									}
								/>
								{overCampaign && <InfoBanner variant="warning" title={overCampaign} />}
								<Select
									label={GOAL_LABEL}
									value={draft.goal}
									onChange={(e) => set({ goal: e.target.value })}
									options={[
										{ value: "", label: GOAL_DEFAULT_OPTION, disabled: false },
										...GOALS[draft.objective].map((g) => ({
											value: g,
											label: GOAL_NAMES[g] ?? g,
											disabled: false,
										})),
									]}
								/>
								<p className="text-xs text-muted">{GOAL_HELP}</p>
								<p className="text-sm text-muted">
									<span className="text-content">{PAY_BY_LABEL}:</span> {PAY_BY_NOTE}
								</p>
								<Select
									label={BID_LABEL}
									value={draft.bidStrategy}
									onChange={(e) => set({ bidStrategy: e.target.value as BidStrategy })}
									options={BID_STRATEGIES[draft.objective].map((b) => ({
										value: b,
										label: BID_NAMES[b],
										disabled: false,
									}))}
								/>
								{draft.objective === "ENGAGEMENTS" && (
									<p className="text-xs text-muted">{TARGET_NOT_FOR_ENGAGEMENTS}</p>
								)}
								{draft.bidStrategy === "AUTO" && <p className="text-sm text-muted">{PRICE_IS_X}</p>}
								{bidNeedsAmount(draft.bidStrategy) && (
									<Input
										label={BID_AMOUNT_LABEL}
										inputMode="decimal"
										value={draft.bidAmount}
										onChange={(e) => set({ bidAmount: e.target.value })}
										helperText={bidAmountHelp(currency)}
										error={
											bidField.kind === "invalid" ? problemText("bid-amount-invalid") : undefined
										}
									/>
								)}
								{draft.bidStrategy === "TARGET" && (
									<InfoBanner variant="warning" title={TARGET_PAY_BY_OPEN} />
								)}
								{overBudget.length > 0 && (
									<InfoBanner variant="warning" title={bidOverBudget(overBudget)} />
								)}
							</div>
							<div className={COLUMN}>
								<fieldset className="grid gap-2">
									<legend className="text-sm text-content">{PLACEMENTS_LABEL}</legend>
									{PLACEMENTS.map((p) => {
										const chosen = draft.placements.includes(p);
										// Search is chosen here and its keyword is asked on the audience step: X
										// requires keyword targeting with it (G:883), checked there (PR 4).
										return (
											<label key={p} className="flex items-start gap-2 text-sm text-content">
												<input
													type="checkbox"
													checked={chosen}
													onChange={(e) =>
														set({
															placements: e.target.checked
																? [...draft.placements, p]
																: draft.placements.filter((k: Placement) => k !== p),
														})
													}
												/>
												<span>
													{PLACEMENT_NAMES[p]}
													<span className="block text-xs text-muted">
														{PLACEMENT_TARGETING[p] ?? ""}
													</span>
												</span>
											</label>
										);
									})}
								</fieldset>
								<p className="text-xs text-muted">{PLACEMENTS_HELP}</p>
								{/*
								 * PR 6: the Delivery block. Every field optional and not sent when empty;
								 * the cap only under an objective X lists for it (R:3126); pacing only with
								 * the budget on each ad group (R:3170: LINE_ITEM parents only).
								 */}
								<fieldset className="grid gap-3">
									<legend className="text-sm text-content">{DELIVERY_HEADING}</legend>
									<p className="text-xs text-muted">{DELIVERY_HELP}</p>
									{capOffered(draft.objective) ? (
										<div className="grid gap-2 sm:grid-cols-2">
											<Input
												label={CAP_LABEL}
												inputMode="numeric"
												value={draft.delivery.frequencyCap}
												onChange={(e) =>
													set({ delivery: { ...draft.delivery, frequencyCap: e.target.value } })
												}
												helperText={CAP_HELP}
											/>
											<Select
												label={WINDOW_LABEL}
												value={draft.delivery.durationInDays}
												onChange={(e) =>
													set({ delivery: { ...draft.delivery, durationInDays: e.target.value } })
												}
												options={[
													{ value: "", label: "—", disabled: false },
													...DURATION_IN_DAYS.map((d) => ({
														value: String(d),
														label: WINDOW_NAMES[String(d)] ?? String(d),
														disabled: false,
													})),
												]}
											/>
										</div>
									) : (
										<p className="text-xs text-muted">{CAP_NOT_FOR_OBJECTIVE}</p>
									)}
									<Select
										label={EXPANSION_LABEL}
										value={draft.delivery.audienceExpansion}
										onChange={(e) =>
											set({ delivery: { ...draft.delivery, audienceExpansion: e.target.value } })
										}
										options={[
											{ value: "", label: EXPANSION_NONE, disabled: false },
											...AUDIENCE_EXPANSION.map((x) => ({ value: x, label: x, disabled: false })),
										]}
									/>
									<p className="text-xs text-muted">{EXPANSION_HELP}</p>
									{draft.budgetOn === "ad-group" && (
										<>
											<Select
												label={PACING_LABEL}
												value={draft.delivery.pacing}
												onChange={(e) =>
													set({
														delivery: {
															...draft.delivery,
															pacing: e.target.value as DeliveryDraft["pacing"],
														},
													})
												}
												options={[
													{ value: "", label: PACING_NONE, disabled: false },
													{ value: "standard", label: PACING_STANDARD, disabled: false },
													{ value: "accelerated", label: PACING_ACCELERATED, disabled: false },
												]}
											/>
											<p className="text-xs text-muted">{PACING_HELP}</p>
										</>
									)}
								</fieldset>
								<p className="text-sm text-muted">{PRODUCT_FIXED}</p>
								<p className="text-sm text-muted">{SCHEDULE_HELP}</p>
								<Input
									type="datetime-local"
									label={START_LABEL}
									value={draft.start}
									onChange={(e) => set({ start: e.target.value })}
									helperText={sentAs(startIso)}
								/>
								<Input
									type="datetime-local"
									label={END_LABEL}
									value={draft.end}
									onChange={(e) => set({ end: e.target.value })}
									helperText={sentAs(endIso)}
								/>
								<p className="text-xs text-muted">{SCHEDULE_PREFILLED}</p>
							</div>
						</section>
					)}

					{shown === 3 && (
						<section className={TWO_COLUMNS}>
							<div className={COLUMN}>
								<p className="text-sm text-muted">{AUDIENCE_HELP}</p>
								<Input
									label={COUNTRY_LABEL}
									value={query}
									onChange={(e) => setQuery(e.target.value)}
									placeholder={COUNTRY_PLACEHOLDER}
								/>
								{searching && <p className="text-xs text-muted">{SEARCHING}</p>}
								{searchFailed && (
									<InfoBanner variant="danger" title="X's countries could not be read.">
										{searchFailed}
									</InfoBanner>
								)}
								{places !== null && (
									<ul className="grid gap-2">
										{places.length === 0 && <li className="text-sm text-muted">{NO_MATCH}</li>}
										{places.map((place) => {
											const chosen = [...draft.countries, ...draft.excludedCountries].some(
												(c) => c.targetingValue === place.targetingValue,
											);
											return (
												<li key={place.targetingValue} className="flex items-center gap-3">
													<span className="text-sm text-content">{place.name}</span>
													<Button
														type="button"
														variant="ghost"
														size="sm"
														disabled={chosen}
														onClick={() =>
															set({ countries: [...draft.countries, place], everyone: false })
														}
													>
														{ADD_COUNTRY}
													</Button>
													<Button
														type="button"
														variant="ghost"
														size="sm"
														disabled={chosen}
														onClick={() =>
															set({ excludedCountries: [...draft.excludedCountries, place] })
														}
													>
														{EXCLUDE_COUNTRY}
													</Button>
												</li>
											);
										})}
									</ul>
								)}
								{draft.countries.length > 0 && (
									<div className="flex flex-wrap items-center gap-2">
										<span className="text-sm text-muted">{COUNTRIES_CHOSEN}</span>
										{draft.countries.map((c) => (
											<span
												key={c.targetingValue}
												className="inline-flex items-center gap-1 rounded-md bg-surface-2 px-2 py-1 text-xs text-content"
											>
												{c.name}
												<button
													type="button"
													className="text-muted hover:text-content"
													aria-label={`${REMOVE_COUNTRY} ${c.name}`}
													onClick={() =>
														set({
															countries: draft.countries.filter(
																(k) => k.targetingValue !== c.targetingValue,
															),
														})
													}
												>
													×
												</button>
											</span>
										))}
									</div>
								)}
								{draft.excludedCountries.length > 0 && (
									<div className="flex flex-wrap items-center gap-2">
										<span className="text-sm text-muted">{COUNTRIES_EXCLUDED}</span>
										{draft.excludedCountries.map((c) => (
											<span
												key={c.targetingValue}
												className="inline-flex items-center gap-1 rounded-md bg-surface-2 px-2 py-1 text-xs text-content"
											>
												{c.name}
												<button
													type="button"
													className="text-muted hover:text-content"
													aria-label={`${REMOVE_COUNTRY} ${c.name}`}
													onClick={() =>
														set({
															excludedCountries: draft.excludedCountries.filter(
																(k) => k.targetingValue !== c.targetingValue,
															),
														})
													}
												>
													×
												</button>
											</span>
										))}
									</div>
								)}
								<p className="text-xs text-muted">{EXCLUDE_HELP}</p>
								<label className="flex items-start gap-2 text-sm text-content">
									<input
										type="checkbox"
										checked={draft.everyone}
										onChange={(e) => set({ everyone: e.target.checked })}
										disabled={draft.countries.length > 0}
									/>
									<span>
										{everyoneLabel(draft.excludedCountries.map((c) => c.name))}
										<span className="block text-xs text-muted">{EVERYONE_NOTE}</span>
									</span>
								</label>

								<p className="text-sm text-muted">{AUDIENCE_MORE_HELP}</p>
								{languagesFailed && (
									<InfoBanner variant="danger" title={LANGUAGES_NOT_READ}>
										{languagesFailed}
									</InfoBanner>
								)}
								<Select
									label={LANGUAGE_LABEL}
									value=""
									placeholder={LANGUAGE_NONE}
									disabled={languageList === null}
									onChange={(e) => {
										const l = languageList?.find((x) => x.targetingValue === e.target.value);
										if (l && !draft.languages.some((k) => k.targetingValue === l.targetingValue))
											set({ languages: [...draft.languages, l] });
									}}
									options={(languageList ?? []).map((l) => ({
										value: l.targetingValue,
										label: languageLabel(l),
									}))}
								/>
								{draft.languages.length > 0 && (
									<div className="flex flex-wrap items-center gap-2">
										<span className="text-sm text-muted">{LANGUAGES_CHOSEN}</span>
										{draft.languages.map((l) => (
											<span
												key={l.targetingValue}
												className="inline-flex items-center gap-1 rounded-md bg-surface-2 px-2 py-1 text-xs text-content"
											>
												{l.name}
												<button
													type="button"
													className="text-muted hover:text-content"
													aria-label={`${REMOVE_COUNTRY} ${l.name}`}
													onClick={() =>
														set({
															languages: draft.languages.filter(
																(k) => k.targetingValue !== l.targetingValue,
															),
														})
													}
												>
													×
												</button>
											</span>
										))}
									</div>
								)}
								<Select
									label={GENDER_LABEL}
									value={draft.gender}
									onChange={(e) => set({ gender: e.target.value })}
									options={[
										{ value: "", label: GENDER_ALL },
										...GENDER_OPTIONS.map((g) => ({ ...g })),
									]}
								/>
								<Select
									label={AGE_LABEL}
									value={draft.age}
									onChange={(e) => set({ age: e.target.value })}
									options={[
										{ value: "", label: AGE_ALL },
										...AGE_BUCKETS.map((a) => ({ value: a, label: ageLabel(a) })),
									]}
								/>
								<p className="text-xs text-muted">{AGE_ONLY_SEEN}</p>
							</div>
							<div className={COLUMN}>
								<p className="text-sm text-muted">{DEVICE_MORE_HELP}</p>
								{platformsFailed && (
									<InfoBanner variant="danger" title={OS_NOT_READ}>
										{platformsFailed}
									</InfoBanner>
								)}
								<Select
									label={OS_LABEL}
									value=""
									placeholder={OS_NONE}
									disabled={platformList === null}
									onChange={(e) => {
										const p = platformList?.find((x) => x.targetingValue === e.target.value);
										if (p && !draft.platforms.some((k) => k.targetingValue === p.targetingValue))
											set({ platforms: [...draft.platforms, p] });
									}}
									options={(platformList ?? []).map((p) => ({
										value: p.targetingValue,
										label: p.name,
									}))}
								/>
								<Chosen
									label={OS_CHOSEN}
									rows={draft.platforms.map((p) => ({ key: p.targetingValue, name: p.name }))}
									remove={(key) =>
										set({ platforms: draft.platforms.filter((p) => p.targetingValue !== key) })
									}
								/>
								<Input
									label={DEVICE_SEARCH_LABEL}
									value={deviceQuery}
									onChange={(e) => setDeviceQuery(e.target.value)}
								/>
								{devicesFailed && (
									<InfoBanner variant="danger" title={DEVICES_NOT_READ}>
										{devicesFailed}
									</InfoBanner>
								)}
								<Select
									label={DEVICE_LABEL}
									value=""
									placeholder={DEVICE_NONE}
									disabled={deviceList === null}
									onChange={(e) => {
										const d = deviceList?.find((x) => x.targetingValue === e.target.value);
										if (d && !draft.devices.some((k) => k.targetingValue === d.targetingValue))
											set({ devices: [...draft.devices, d] });
									}}
									options={(deviceList ?? []).map((d) => ({
										value: d.targetingValue,
										label: deviceLabel(d),
									}))}
								/>
								<Chosen
									label={DEVICES_CHOSEN}
									rows={draft.devices.map((d) => ({ key: d.targetingValue, name: deviceLabel(d) }))}
									remove={(key) =>
										set({ devices: draft.devices.filter((d) => d.targetingValue !== key) })
									}
								/>
								<p className="text-xs text-muted">{PLATFORM_DEVICE_OVERLAP}</p>
								{carrierCountries.length === 0 ? (
									<p className="text-xs text-muted">{CARRIER_NEEDS_COUNTRY}</p>
								) : (
									<>
										<Select
											label={CARRIER_COUNTRY_LABEL}
											value={carrierCountry}
											placeholder={CARRIER_NEEDS_COUNTRY}
											onChange={(e) => setCarrierCountry(e.target.value)}
											options={carrierCountries.map((c) => ({ value: c, label: c }))}
										/>
										{carriersFailed && (
											<InfoBanner variant="danger" title={CARRIERS_NOT_READ}>
												{carriersFailed}
											</InfoBanner>
										)}
										<Select
											label={CARRIER_LABEL}
											value=""
											placeholder={CARRIER_NONE}
											disabled={carrierList === null}
											onChange={(e) => {
												const o = carrierList?.find((x) => x.targetingValue === e.target.value);
												if (o && !draft.carriers.some((k) => k.targetingValue === o.targetingValue))
													set({ carriers: [...draft.carriers, o] });
											}}
											options={(carrierList ?? []).map((o) => ({
												value: o.targetingValue,
												label: carrierLabel(o),
											}))}
										/>
									</>
								)}
								<Chosen
									label={CARRIERS_CHOSEN}
									rows={draft.carriers.map((o) => ({
										key: o.targetingValue,
										name: carrierLabel(o),
									}))}
									remove={(key) =>
										set({ carriers: draft.carriers.filter((o) => o.targetingValue !== key) })
									}
								/>
								<PrimaryTargeting value={draft} onChange={set} />

								<p className="text-sm text-muted">{AUDIENCES_HELP}</p>
								{audiencesFailed && (
									<InfoBanner variant="danger" title={AUDIENCES_NOT_READ}>
										{audiencesFailed}
									</InfoBanner>
								)}
								<Select
									label={AUDIENCE_LABEL}
									value=""
									placeholder={AUDIENCE_NONE}
									disabled={targetableAudiences.length === 0}
									onChange={(e) => {
										const a = targetableAudiences.find((x) => x.id === e.target.value);
										if (a && !draft.audiences.some((k) => k.id === a.id))
											set({ audiences: [...draft.audiences, a] });
									}}
									options={targetableAudiences.map((a) => ({ value: a.id, label: a.name }))}
								/>
								{audiencesHere !== null && audiencesHere.length === 0 && (
									<p className="text-xs text-muted">{AUDIENCES_EMPTY}</p>
								)}
								{untargetableAudiences.length > 0 && (
									<div className="text-xs text-muted">
										<span>{AUDIENCES_NOT_TARGETABLE}</span>
										<ul className="list-disc pl-5">
											{untargetableAudiences.map((a) => (
												<li key={a.id}>{audienceNotTargetable(a)}</li>
											))}
										</ul>
									</div>
								)}
								<Chosen
									label={AUDIENCES_CHOSEN}
									rows={draft.audiences.map((a) => ({ key: a.id, name: a.name }))}
									remove={(key) => set({ audiences: draft.audiences.filter((a) => a.id !== key) })}
								/>
							</div>
						</section>
					)}

					{shown === 4 && (
						<section className="grid gap-4">
							<AdStep
								key={xAccountId}
								xAccountId={xAccountId}
								objective={draft.objective}
								draft={draft}
								set={set}
							/>
						</section>
					)}

					{shown === 5 && account && (
						<section className="grid gap-4">
							<p className="text-sm text-muted">{REVIEW_HELP}</p>
							{warning && (
								<InfoBanner variant="warning" title={warning.title}>
									{warning.body}
								</InfoBanner>
							)}
							<p className="text-sm text-muted">{RUN_ORDER}</p>
							{!writable && <p className="text-sm text-muted">{READ_ONLY}</p>}
							{writable && (
								<div>
									<Button
										type="button"
										loading={creating}
										disabled={locked}
										onClick={() => void create()}
									>
										{CREATE_LABEL}
									</Button>
								</div>
							)}

							{run && (
								<ol className="grid gap-2 rounded-lg border border-line p-4 text-sm">
									{(
										[
											["campaign", "campaign", RUN_ROWS.campaign, run.campaign],
											["lineItem", "lineItem", RUN_ROWS.lineItem, run.lineItem],
											...run.targeting.map(
												(t) =>
													[
														"targeting",
														`targeting-${t.targetingType}-${t.targetingValue}`,
														`${RUN_ROWS.targeting} — ${t.name}`,
														t.state,
													] as const,
											),
											...run.media.map(
												(m, i) =>
													[
														"media",
														`media-${i}-${m.name}`,
														`${RUN_ROWS.media} — ${m.name}`,
														m.state,
													] as const,
											),
											...(run.card === null
												? []
												: [["card", "card", RUN_ROWS.card, run.card] as const]),
											...(run.tweet === null
												? []
												: [["tweet", "tweet", RUN_ROWS.tweet, run.tweet] as const]),
											["ad", "ad", RUN_ROWS.ad, run.ad],
										] as const
									).map(([what, key, label, state]) => {
										const line = stepText(what, state);
										return (
											<li key={key} className="grid gap-1">
												<span className="font-medium text-content">{label}</span>
												<span className="flex items-center gap-2 text-muted">
													{state.kind === "running" && <Spinner size="sm" />}
													{line.text}
												</span>
												{line.detail && <span className="text-xs text-muted">{line.detail}</span>}
											</li>
										);
									})}
								</ol>
							)}

							{run &&
								runStopped(run) &&
								(() => {
									const stopped = stoppedText(createdSoFar(run), somethingMayExist(run));
									return (
										<InfoBanner variant="warning" title={stopped.title}>
											{stopped.body}{" "}
											<Link to="/campaigns" className="font-medium underline">
												{OPEN_IN_CAMPAIGNS}
											</Link>
										</InfoBanner>
									);
								})()}

							{run && runDone(run) && (
								<InfoBanner variant="success" title={DONE.title}>
									{DONE.body}{" "}
									<Link to="/campaigns" className="font-medium underline">
										{OPEN_IN_CAMPAIGNS}
									</Link>
								</InfoBanner>
							)}
						</section>
					)}
				</Panel>

				<Panel title={STEP_NAMES[5]}>
					{/* What will be sent, on every step: the Review step's rows, beside the fields that fill them. */}
					{account && (
						<dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-2 text-sm">
							<dt className="text-muted">{REVIEW_ROWS.account}</dt>
							<dd className="text-content">
								{account.name} · {account.xAccountId}
							</dd>
							<dt className="text-muted">{REVIEW_ROWS.campaign}</dt>
							<dd className="text-content">{draft.name}</dd>
							<dt className="text-muted">{REVIEW_ROWS.instrument}</dt>
							<dd className="text-content">
								{instrument ? (instrument.description ?? instrument.id) : draft.fundingInstrumentId}
							</dd>
							<dt className="text-muted">{REVIEW_ROWS.budget}</dt>
							<dd className="text-content">
								{budgetSummary(
									dailyField.kind === "amount" ? dailyField.micro : null,
									totalField.kind === "amount" ? totalField.micro : null,
									currency,
								)}
							</dd>
							<dt className="text-muted">{REVIEW_ROWS.adGroup}</dt>
							<dd className="text-content">
								{objectiveSummary(draft.objective)}
								{draft.lineItemName.trim() !== "" && ` — named «${draft.lineItemName.trim()}»`}
								{adGroupTotalField.kind === "amount" &&
									` — total ${microMoney(adGroupTotalField.micro, currency)}`}
								{` — ${goalSummary(draft.goal)}; ${bidSummary(
									draft.bidStrategy,
									bidField.kind === "amount" ? bidField.micro : null,
									currency,
								)}; ${placementsSummary(draft.placements)}`}
							</dd>
							<dt className="text-muted">{REVIEW_ROWS.schedule}</dt>
							<dd className="text-content">
								{scheduleSummary(
									{ local: draft.start, iso: startIso },
									{ local: draft.end, iso: endIso },
								)}
							</dd>
							<dt className="text-muted">{REVIEW_ROWS.countries}</dt>
							<dd className="text-content">
								{countriesSummary(
									draft.countries.map((c) => c.name),
									draft.everyone,
									draft.excludedCountries.map((c) => c.name),
								)}
							</dd>
							<dt className="text-muted">{REVIEW_ROWS.excludedCountries}</dt>
							<dd className="text-content">
								{optionalSummary(draft.excludedCountries.map((c) => c.name))}
							</dd>
							<dt className="text-muted">{REVIEW_ROWS.languages}</dt>
							<dd className="text-content">
								{optionalSummary(draft.languages.map((l) => l.name))}
							</dd>
							<dt className="text-muted">{REVIEW_ROWS.gender}</dt>
							<dd className="text-content">
								{optionalSummary(
									GENDER_OPTIONS.filter((g) => g.value === draft.gender).map((g) => g.label),
								)}
							</dd>
							<dt className="text-muted">{REVIEW_ROWS.age}</dt>
							<dd className="text-content">
								{optionalSummary(draft.age === "" ? [] : [ageLabel(draft.age)])}
							</dd>
							<dt className="text-muted">{REVIEW_ROWS.operatingSystems}</dt>
							<dd className="text-content">
								{optionalSummary(draft.platforms.map((p) => p.name))}
							</dd>
							<dt className="text-muted">{REVIEW_ROWS.devices}</dt>
							<dd className="text-content">{optionalSummary(draft.devices.map(deviceLabel))}</dd>
							<dt className="text-muted">{REVIEW_ROWS.carriers}</dt>
							<dd className="text-content">{optionalSummary(draft.carriers.map(carrierLabel))}</dd>
							<dt className="text-muted">{REVIEW_ROWS.audiences}</dt>
							<dd className="text-content">
								{optionalSummary(draft.audiences.map((a) => a.name))}
							</dd>
							<dt className="text-muted">{REVIEW_ROWS.ad}</dt>
							<dd className="text-content">
								{draft.adMode === "write"
									? writtenAdSummary(
											tweetText(draft) ?? "",
											draft.adName.trim() === "" ? defaultTweetName(draft.adText) : draft.adName,
											draft.adFiles,
										)
									: draft.tweet
										? tweetLabel(draft.tweet)
										: "—"}
							</dd>
						</dl>
					)}
					{/*
					 * 🔴 Start under the same conditions as before the move: on the Review step, under
					 * an account, once the run is done — not on a step Back returned to after it.
					 */}
					{shown === 5 && account && run && runDone(run) && (
						<section className="grid gap-3 border-t border-line pt-3">
							<h2 className="text-sm font-semibold text-content">{START_HEADING}</h2>
							<p className="text-sm text-muted">{START_ORDER}</p>
							<p className="text-sm text-muted">{START_IS_NOT_DELIVERY}</p>
							<div className="flex flex-wrap items-center gap-3">
								<Button
									type="button"
									variant="outline"
									size="sm"
									disabled={switching || lineItemStarted}
									onClick={() => void startLineItem()}
								>
									{START_AD_GROUP}
								</Button>
								{lineItemSwitch && <Badge variant="default">{lineItemSwitch}</Badge>}
							</div>
							{pending && (
								<InfoBanner variant="warning" title="Start this line item as it is?">
									{pending.text}
								</InfoBanner>
							)}
							<div className="flex flex-wrap items-center gap-3">
								<Button
									type="button"
									variant="primary"
									size="sm"
									disabled={switching || !lineItemStarted}
									onClick={() => void startCampaign()}
								>
									{START_CAMPAIGN}
								</Button>
								{!lineItemStarted && (
									<span className="text-xs text-muted">{START_CAMPAIGN_WAITS}</span>
								)}
							</div>
							{campaignSwitch && (
								<InfoBanner variant={campaignSwitch.variant} title={campaignSwitch.title}>
									{campaignSwitch.body}
								</InfoBanner>
							)}
						</section>
					)}
				</Panel>
			</div>

			<Modal
				open={confirming !== null}
				title={START_CAMPAIGN}
				confirmLabel={START_CAMPAIGN}
				cancelLabel={CANCEL}
				onConfirm={() => void sendCampaignStart()}
				onCancel={() => setConfirming(null)}
			>
				{confirming}
			</Modal>
		</Page>
	);
}

/** The chips under a multi-choice field: what is chosen, each with its ×. Nothing when empty. */
function Chosen({
	label,
	rows,
	remove,
}: {
	label: string;
	rows: { key: string; name: string }[];
	remove: (key: string) => void;
}) {
	if (rows.length === 0) return null;
	return (
		<div className="flex flex-wrap items-center gap-2">
			<span className="text-sm text-muted">{label}</span>
			{rows.map((r) => (
				<span
					key={r.key}
					className="inline-flex items-center gap-1 rounded-md bg-surface-2 px-2 py-1 text-xs text-content"
				>
					{r.name}
					<button
						type="button"
						className="text-muted hover:text-content"
						aria-label={`${REMOVE_COUNTRY} ${r.name}`}
						onClick={() => remove(r.key)}
					>
						×
					</button>
				</span>
			))}
		</div>
	);
}
